import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signIn, supabase } from '../lib/supabase';
import PasswordInput from '../components/PasswordInput';
import './SignIn.css';

const SignIn = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const { data, error: signInError } = await signIn(email, password);

    if (signInError) {
      setError(signInError.message);
      setLoading(false);
      return;
    }

    if (data.user) {
      // Route by role:
      //   master_admin (no shop)  → /admin/shops
      //   barber                  → /diary (their portal)
      //   shop_admin (first login,
      //     onboarding not done)  → /welcome
      //   shop_admin (returning)  → /dashboard
      //   everyone else           → /dashboard
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('role, shop_id')
        .eq('id', data.user.id)
        .maybeSingle();
      // master_admin always routes to /admin/shops even if a stray
      // shop_id ended up on their profile (data drift, dev seed, etc).
      // Master admin is a role identity — not a per-shop attachment.
      const isMasterAdmin = profile?.role === 'master_admin';
      const isBarber = profile?.role === 'barber';
      const isShopAdmin = profile?.role === 'shop_admin' && !!profile?.shop_id;

      let target = '/dashboard';
      if (isMasterAdmin) target = '/admin/dashboard';
      else if (isBarber) target = '/diary';
      else if (isShopAdmin) {
        // Check if the shop has completed onboarding
        const { data: shopRow } = await supabase
          .from('shops')
          .select('onboarding_completed_at')
          .eq('id', profile.shop_id)
          .maybeSingle();
        if (shopRow && !shopRow.onboarding_completed_at) target = '/welcome';
      }
      navigate(target);
    }
    setLoading(false);
  };

  return (
    <div className="auth">
      <div className="auth-l">
        <div className="streak" style={{ top: '30%', right: '-20px', width: '240px' }}></div>
        <div className="streak" style={{ top: 'calc(30% + 12px)', right: '-20px', width: '160px', opacity: '0.5' }}></div>
        <div className="brandrow">
          <img className="mark" src="/logos/instacut-mark-28.svg" alt="" width="28" height="28" />
          Instacut
        </div>
        <h3>Run your chair,<br />not your phone.</h3>
        <p>Bookings, deposits and reminders — handled. Sign in to your shop dashboard.</p>
        <div className="quote">"Cut my no-shows in half in the first month." — Marcus, Sharp & Sons</div>
      </div>
      
      <div className="auth-r">
        <h4>Welcome back</h4>
        <div className="s">Sign in to manage your shop.</div>
        
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="field">
            <label>Email</label>
            <input 
              className="inp" 
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yourshop.co.uk"
              required
              disabled={loading}
              data-testid="signin-email"
            />
          </div>
          
          <div className="field">
            <label>Password</label>
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              disabled={loading}
              data-testid="signin-password"
              toggleTestId="signin-password-toggle"
            />
          </div>
          
          <div className="row">
            <label style={{ display: 'flex', alignItems: 'center', gap: '7px', fontWeight: 400, color: 'var(--mute)' }}>
              <input 
                type="checkbox" 
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              /> 
              Remember me
            </label>
            <a href="/forgot" data-testid="signin-forgot-link">Forgot password?</a>
          </div>
          
          <button 
            className="btn p" 
            type="submit"
            disabled={loading}
            data-testid="signin-submit"
            style={{ width: '100%', justifyContent: 'center', padding: '12px' }}
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
        
        <div style={{ textAlign: 'center', fontSize: '12.5px', color: 'var(--mute)', marginTop: '18px' }}>
          New shop? <a href="/signup" style={{ color: 'var(--acc)', fontWeight: 600, textDecoration: 'none' }} data-testid="signin-signup-link">Sign up →</a>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
